import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Breadcrumb } from '../ui/breadcrumb';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FileUpload } from '../ui/FileUpload';
import { 
  ArrowLeft, 
  Crown, 
  User, 
  Calendar, 
  CreditCard, 
  FileText, 
  Settings, 
  LogOut,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  Lock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { authUtils } from '../../utils/authUtils';
import { logger } from '../../utils/logger';

export function SubscriptionDashboard() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [assignmentForm, setAssignmentForm] = useState({
    workType: 'assignment',
    pages: '',
    deadline: '',
    requirements: ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    logger.log('SubscriptionDashboard component mounted');
    // Get user data from stored auth tokens
    const storedData = authUtils.getStoredAuthData();
    logger.log('Stored auth data:', storedData);
    if (storedData && storedData.user) {
      setUserData(storedData.user);
      logger.log('User data set:', storedData.user);
    } else {
      logger.log('No user data found, redirecting to login');
      // Redirect to login if no user data
      navigate('/learn/customer-work/subscription');
    }
  }, [navigate]);

  const handleLogout = () => {
    authUtils.clearAuthTokens();
    navigate('/learn/customer-work/subscription');
  };

  const handleAssignmentFormChange = (field, value) => {
    setAssignmentForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateAssignmentForm = () => {
    const errors = {};
    
    if (!assignmentForm.pages.trim() || parseInt(assignmentForm.pages) <= 0) {
      errors.pages = 'Please enter a valid number of pages';
    }
    
    if (!assignmentForm.deadline) {
      errors.deadline = 'Please select a deadline';
    }
    
    if (!uploadedFile) {
      errors.file = 'Please upload your assignment files';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAssignmentForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`Assignment submitted successfully! 
      
Work Type: ${assignmentForm.workType}
Pages: ${assignmentForm.pages}
Deadline: ${assignmentForm.deadline}
File: ${uploadedFile.name}

Your assignment has been added to the queue and will be processed with priority as part of your subscription benefits.`);
      
      // Reset form
      setAssignmentForm({
        workType: 'assignment',
        pages: '',
        deadline: '',
        requirements: ''
      });
      setUploadedFile(null);
      
    } catch (error) {
      logger.error('Assignment submission error:', error);
      alert('Error submitting assignment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Learn & Earn', href: '/learn' },
    { label: 'Customer Work', href: '/learn/customer-work' },
    { label: 'Subscription Dashboard' }
  ];

  if (!userData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/30 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <Breadcrumb items={breadcrumbItems} />
          <Button
            variant="ghost"
            onClick={() => navigate('/learn/customer-work')}
            className="mb-6 hover:bg-accent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Services
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-between items-start mb-8"
        >
          <div>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary px-6 py-2 rounded-full mb-4">
              <Crown className="w-5 h-5" />
              <span className="text-sm font-semibold">Subscription Dashboard</span>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Welcome back, {userData.name || userData.username}!
            </h1>
            <p className="text-muted-foreground text-lg">
              Manage your subscription and access premium services
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2 mb-8 border-b border-border"
        >
          {[
            { id: 'overview', label: 'Overview', icon: User },
            { id: 'subscription', label: 'Subscription', icon: Crown },
            { id: 'assignments', label: 'Assignments', icon: FileText },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2"
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Current Plan */}
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Crown className="w-5 h-5" />
                    Current Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground mb-2">
                    {userData.planName || '6 Months'}
                  </div>
                  <div className="text-lg text-primary font-semibold mb-4">
                    {userData.planAmount || '₹500'}
                  </div>
                  <Badge variant="secondary" className="bg-green-500/10 text-green-400 border-green-500/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                </CardContent>
              </Card>

              {/* Account Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Account Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Username</p>
                    <p className="font-medium text-foreground">{userData.username}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium text-foreground">{userData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Member Since</p>
                    <p className="font-medium text-foreground">January 2025</p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Assignments</span>
                    <span className="font-medium text-foreground">12</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Completed</span>
                    <span className="font-medium text-green-400">8</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">In Progress</span>
                    <span className="font-medium text-yellow-400">3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pending</span>
                    <span className="font-medium text-blue-400">1</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5" />
                    Subscription Details
                  </CardTitle>
                  <CardDescription>
                    Manage your subscription plan and billing
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-foreground mb-3">Current Plan</h3>
                      <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-lg font-bold text-foreground">
                            {userData.planName || '6 Months Plan'}
                          </span>
                          <Badge variant="secondary" className="bg-green-500/10 text-green-400 border-green-500/20">
                            Active
                          </Badge>
                        </div>
                        <div className="text-2xl font-bold text-primary mb-2">
                          {userData.planAmount || '₹500'}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Next billing: February 31, 2025
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-3">Plan Features</h3>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">Priority support</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">Fast delivery</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">Unlimited revisions</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">Dedicated support</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                      Upgrade Plan
                    </Button>
                    <Button variant="outline">
                      View Billing History
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'assignments' && (
            <div className="space-y-6">
              {/* Assignment Upload Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Submit New Assignment
                  </CardTitle>
                  <CardDescription>
                    Upload your assignment or journal work - No additional payment required with your subscription
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAssignmentSubmit} className="space-y-6">
                    {/* Work Type Selection */}
                    <div>
                      <Label className="text-foreground font-semibold mb-3 block">What type of work do you want to submit?</Label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div 
                          className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-colors ${
                            assignmentForm.workType === 'assignment' 
                              ? 'border-green-500 bg-green-500/10' 
                              : 'border-border bg-card hover:border-green-500/50'
                          }`}
                          onClick={() => handleAssignmentFormChange('workType', 'assignment')}
                        >
                          <input 
                            type="radio" 
                            name="workType" 
                            value="assignment" 
                            checked={assignmentForm.workType === 'assignment'}
                            onChange={() => handleAssignmentFormChange('workType', 'assignment')}
                            className="text-green-500" 
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-green-500" />
                              <span className="font-semibold">Assignment</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Get your assignment completed</p>
                          </div>
                        </div>
                        <div 
                          className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-colors ${
                            assignmentForm.workType === 'journal' 
                              ? 'border-green-500 bg-green-500/10' 
                              : 'border-border bg-card hover:border-green-500/50'
                          }`}
                          onClick={() => handleAssignmentFormChange('workType', 'journal')}
                        >
                          <input 
                            type="radio" 
                            name="workType" 
                            value="journal" 
                            checked={assignmentForm.workType === 'journal'}
                            onChange={() => handleAssignmentFormChange('workType', 'journal')}
                            className="text-green-500" 
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-green-500" />
                              <span className="font-semibold">Journal</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Get your journal written</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* File Upload */}
                    <div>
                      <Label className="text-foreground font-semibold flex items-center gap-2 mb-3">
                        <Upload className="w-4 h-4 text-green-500" />
                        Upload Your Work Files
                      </Label>
                      <FileUpload 
                        onFileSelect={setUploadedFile}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        maxSize={10}
                      />
                      {formErrors.file && (
                        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {formErrors.file}
                        </p>
                      )}
                    </div>

                    {/* Assignment Details */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pages" className="text-foreground font-semibold">Number of Pages</Label>
                        <Input
                          id="pages"
                          type="number"
                          min="1"
                          value={assignmentForm.pages}
                          onChange={(e) => handleAssignmentFormChange('pages', e.target.value)}
                          placeholder="Enter number of pages"
                          className={`mt-2 ${formErrors.pages ? 'border-red-500' : 'border-border focus:border-green-500 focus:ring-green-500'}`}
                        />
                        {formErrors.pages && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {formErrors.pages}
                          </p>
                        )}
                        <p className="text-xs text-green-600 mt-1 font-medium">✅ No additional charges - Included in your subscription</p>
                      </div>
                      <div>
                        <Label htmlFor="deadline" className="text-foreground font-semibold">Completion Deadline</Label>
                        <Input
                          id="deadline"
                          type="date"
                          value={assignmentForm.deadline}
                          onChange={(e) => handleAssignmentFormChange('deadline', e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className={`mt-2 ${formErrors.deadline ? 'border-red-500' : 'border-border focus:border-green-500 focus:ring-green-500'}`}
                        />
                        {formErrors.deadline && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {formErrors.deadline}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <Label htmlFor="requirements" className="text-foreground font-semibold mb-3 block">
                        Specific Requirements or Instructions
                      </Label>
                      <textarea
                        id="requirements"
                        value={assignmentForm.requirements}
                        onChange={(e) => handleAssignmentFormChange('requirements', e.target.value)}
                        className="w-full min-h-[120px] p-4 border border-border focus:border-green-500 focus:ring-green-500 bg-background rounded-xl resize-vertical"
                        placeholder="Please provide any specific requirements, instructions, or additional details about your work...

Examples:
• Specific formatting requirements
• Reference style (APA, MLA, etc.)
• Special instructions
• Preferred writing style
• Any other important details"
                        rows={5}
                      />
                    </div>

                    {/* Subscription Benefits Notice */}
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                        <div>
                          <p className="text-green-700 font-semibold">Subscription Benefits Active</p>
                          <p className="text-green-600 text-sm">No payment required • Priority processing • Unlimited submissions until {userData.planName} expires</p>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 font-semibold disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Submitting Assignment...
                        </>
                      ) : (
                        'Submit Assignment'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Recent Assignments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Your Recent Assignments
                  </CardTitle>
                  <CardDescription>
                    Track your assignment progress and submissions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { 
                        id: 1, 
                        title: 'React Component Development', 
                        type: 'Assignment',
                        pages: 8,
                        status: 'completed', 
                        submittedDate: '2025-01-25',
                        completedDate: '2025-01-28',
                        amount: 'Included in subscription'
                      },
                      { 
                        id: 2, 
                        title: 'Database Design Project', 
                        type: 'Assignment',
                        pages: 12,
                        status: 'in-progress', 
                        submittedDate: '2025-01-28',
                        expectedDate: '2025-01-30',
                        amount: 'Included in subscription'
                      },
                      { 
                        id: 3, 
                        title: 'Research Journal Entry', 
                        type: 'Journal',
                        pages: 5,
                        status: 'pending', 
                        submittedDate: '2025-01-30',
                        expectedDate: '2025-02-01',
                        amount: 'Included in subscription'
                      }
                    ].map((assignment) => (
                      <div key={assignment.id} className="p-4 border border-border rounded-lg hover:border-green-500/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-foreground">{assignment.title}</h4>
                              <Badge variant="secondary" className="text-xs">
                                {assignment.type}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                              <div>
                                <span className="font-medium">Pages:</span> {assignment.pages}
                              </div>
                              <div>
                                <span className="font-medium">Submitted:</span> {assignment.submittedDate}
                              </div>
                              <div>
                                <span className="font-medium">
                                  {assignment.status === 'completed' ? 'Completed:' : 'Expected:'}
                                </span> {assignment.completedDate || assignment.expectedDate}
                              </div>
                              <div>
                                <span className="font-medium text-green-600">{assignment.amount}</span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            <Badge 
                              variant="secondary" 
                              className={
                                assignment.status === 'completed' ? 'bg-green-500/10 text-green-600 border-green-500/20' :
                                assignment.status === 'in-progress' ? 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' :
                                'bg-blue-500/10 text-blue-600 border-blue-500/20'
                              }
                            >
                              {assignment.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {assignment.status === 'in-progress' && <Clock className="w-3 h-3 mr-1" />}
                              {assignment.status === 'pending' && <AlertCircle className="w-3 h-3 mr-1" />}
                              {assignment.status.replace('-', ' ')}
                            </Badge>
                          </div>
                        </div>
                        
                        {assignment.status === 'completed' && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <Button variant="outline" size="sm" className="mr-2">
                              Download Result
                            </Button>
                            <Button variant="outline" size="sm">
                              View Details
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-500/10">
                      Load More Assignments
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Account Settings
                  </CardTitle>
                  <CardDescription>
                    Manage your account preferences and security
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button variant="outline" className="w-full justify-start">
                    <User className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payment Methods
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Calendar className="w-4 h-4 mr-2" />
                    Notification Preferences
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}